import React, { useCallback, useEffect, useState } from 'react';
import { Chip, CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  GppGood as SignIcon,
  Print as PrintIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import SignPrescriptionDialog from '../clinic/SignPrescriptionDialog';
import SendDocumentDialog from '../SendDocumentDialog';
import { MemedOfficialDocumentButton } from '../clinic/MemedPrescriptionBadge';
import type { SignedDocumentInfo } from '../../services/documentSignatureService';
import { fetchLinkedPrescription, printSingleLinkedPrescription } from '../../utils/evolutionPrescriptionPrint';
import {
  PRESCRIPTIONS_SIGNED_EVENT,
  isSignedPrescription,
  notifyPrescriptionsSigned,
} from '../../utils/prescriptionSignature';

interface Props {
  prescriptionId: string;
  isMemed?: boolean;
  /** Fallback quando o receituário ainda não carregou os dados do paciente. */
  patientId?: string | null;
  onError: (message: string) => void;
  onSigned?: (info: SignedDocumentInfo) => void;
}

/**
 * Ações do receituário dentro da evolução: assinar (ICP), abrir PDF assinado ou
 * imprimir rascunho — o mesmo fluxo da página Receituários.
 */
const EvolutionPrescriptionActions: React.FC<Props> = ({
  prescriptionId,
  isMemed = false,
  patientId: patientIdFallback,
  onError,
  onSigned,
}) => {
  const [prescription, setPrescription] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [signOpen, setSignOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const reload = useCallback(async () => {
    if (!prescriptionId || isMemed) return;
    setLoading(true);
    try {
      setPrescription(await fetchLinkedPrescription(prescriptionId));
    } catch {
      setPrescription(null);
    } finally {
      setLoading(false);
    }
  }, [prescriptionId, isMemed]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const onSignedElsewhere = (event: Event) => {
      const ids = (event as CustomEvent<{ ids?: string[] }>).detail?.ids || [];
      if (ids.includes(prescriptionId)) void reload();
    };
    window.addEventListener(PRESCRIPTIONS_SIGNED_EVENT, onSignedElsewhere);
    return () => window.removeEventListener(PRESCRIPTIONS_SIGNED_EVENT, onSignedElsewhere);
  }, [prescriptionId, reload]);

  const signed = Boolean(prescription && isSignedPrescription(prescription));

  const handlePrint = async () => {
    if (isMemed) return;
    setWorking(true);
    try {
      await printSingleLinkedPrescription(prescriptionId);
    } catch (err: any) {
      onError(err?.message || 'Não foi possível imprimir o receituário.');
    } finally {
      setWorking(false);
    }
  };

  const handleSigned = async (info: SignedDocumentInfo) => {
    setSignOpen(false);
    await reload();
    onSigned?.(info);
    notifyPrescriptionsSigned([prescriptionId]);
    setSendOpen(true);
  };

  if (isMemed) {
    return (
      <MemedOfficialDocumentButton
        prescriptionId={prescriptionId}
        onError={onError}
      />
    );
  }

  return (
    <>
      {loading ? (
        <CircularProgress size={18} sx={{ mx: 0.5 }} />
      ) : null}
      {signed ? (
        <Chip
          size="small"
          label="Assinada"
          icon={<SignIcon sx={{ fontSize: '0.85rem !important' }} />}
          sx={{
            height: 24,
            mr: 0.25,
            bgcolor: '#dcfce7',
            color: '#15803d',
            fontWeight: 700,
            '& .MuiChip-icon': { color: '#16a34a' },
          }}
        />
      ) : (
        <Tooltip title="Assinar com certificado digital ICP-Brasil (A1 ou A3)">
          <span>
            <IconButton
              size="small"
              disabled={working || !prescription?.professional_id}
              onClick={() => setSignOpen(true)}
              aria-label="Assinar receituário"
            >
              <SignIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title={signed ? 'Abrir PDF assinado' : 'Imprimir receituário'}>
        <span>
          <IconButton
            size="small"
            disabled={working}
            onClick={() => void handlePrint()}
            aria-label={signed ? 'Abrir PDF assinado' : 'Imprimir receituário'}
          >
            <PrintIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {signed && (
        <Tooltip title="Enviar receituário assinado para o paciente">
          <span>
            <IconButton
              size="small"
              disabled={working}
              onClick={() => setSendOpen(true)}
              aria-label="Enviar receituário assinado"
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <SignPrescriptionDialog
        open={signOpen}
        prescription={prescription}
        onClose={() => setSignOpen(false)}
        onSigned={(info) => void handleSigned(info)}
      />
      <SendDocumentDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        documentType="prescription"
        documentId={prescriptionId}
        patientId={prescription?.patient_id || patientIdFallback || undefined}
        patientEmail={prescription?.patient_email}
        patientPhone={prescription?.patient_phone}
        patientWhatsapp={prescription?.patient_whatsapp}
        patientName={prescription?.patient_name}
        documentLabel="Receituário assinado"
      />
    </>
  );
};

export default EvolutionPrescriptionActions;
